import React, { useEffect, useState } from 'react';
import { Plus, Calendar as CalendarIcon, Clock, MapPin, ArrowRight, Repeat } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Event } from '../types/supabase';
import AddEventModal from '../components/AddEventModal';
import Calendar from '../components/Calendar';
import { EventSkeleton } from '../components/LoadingSkeleton';
import { SEO } from '../components/SEO';
import SignupModal, { SignupKind } from '../components/SignupModal';
import SignupButtons from '../components/SignupButtons';
import { fetchSignupCounts, CountMap } from '../lib/eventSignups';
import { LinkifyText } from '../components/LinkifyText';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { describeRecurrence, getNextOccurrence } from '../lib/recurrence';

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [signup, setSignup] = useState<{ event: Event; kind: SignupKind } | null>(null);
  const [volunteerCounts, setVolunteerCounts] = useState<CountMap>({});
  const [attendeeCounts, setAttendeeCounts] = useState<CountMap>({});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvents();
  }, []);

  const loadSignupCounts = async (eventIds: string[]) => {
    const { volunteers, attendees } = await fetchSignupCounts(eventIds);
    setVolunteerCounts(volunteers);
    setAttendeeCounts(attendees);
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'approved')
        .order('date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
      if (data && data.length > 0) {
        loadSignupCounts(data.map((e) => e.id));
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
    
    // Scroll the selected event into view if it's in the list
    const eventElement = document.getElementById(`event-${event.id}`);
    if (eventElement) {
      eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Filter future events and sort by date
  const upcomingEvents = events
    .map(event => ({ event, nextDate: getNextOccurrence(event) }))
    .filter((item): item is { event: Event; nextDate: string } => item.nextDate !== null)
    .sort((a, b) => a.nextDate.localeCompare(b.nextDate) || a.event.time.localeCompare(b.event.time));

  const renderEventCard = ({ event, nextDate }: { event: Event; nextDate: string }) => {
    const repeats = describeRecurrence(event);
    return (
    <div
      key={event.id}
      id={`event-${event.id}`}
      className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow ${
        selectedEvent?.id === event.id ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <h3 className="text-xl font-semibold text-gray-900 mb-3">
        {event.title}
      </h3>
      {repeats && (
        <span className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-sm font-medium">
          <Repeat className="w-3.5 h-3.5" />
          {repeats}
        </span>
      )}
      <div className="space-y-2 text-gray-600">
        <div className="flex items-center">
          <CalendarIcon className="w-5 h-5 mr-2 text-blue-600" />
          <span>
            {repeats && <span className="font-medium text-gray-700">Next: </span>}
            {format(parseISO(nextDate), 'EEEE, MMMM d, yyyy')}
          </span>
        </div>
        <div className="flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-600" />
          <span>{event.time}</span>
        </div>
        <div className="flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-blue-600" />
          <span>{event.location}</span>
        </div>
      </div>
      <p className="mt-4 text-gray-700 line-clamp-3"><LinkifyText text={event.description} /></p>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Link
          to={`/events/${event.id}`}
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors text-sm"
        >
          View Details
          <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
        <SignupButtons
          event={event}
          volunteerCount={volunteerCounts[event.id] ?? 0}
          attendeeCount={attendeeCounts[event.id] ?? 0}
          onSelect={(kind) => setSignup({ event, kind })}
        />
      </div>
    </div>
    );
  };

  return (
    <>
      <SEO 
        title="Events | Monday Night Group"
        description="Join us for special events and gatherings in Nashville. View our calendar and request to add your own events to our men's community."
        image="https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630"
        url="https://mondaynightgroup.com/events"
      />

      {/* Hero Section */}
      <section 
        className="relative h-[50vh] bg-gradient-to-r from-blue-900 to-blue-700 flex items-center"
        style={{ 
          backgroundImage: 'url(/Screenshot_2026-09-20_at_8.24.29_PM.png)', 
          backgroundSize: 'cover',
          backgroundPosition: 'top'
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Special Events
            </h1>
            <p className="text-xl text-white">
              Join us for these unique gatherings and special occasions
            </p>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 bg-white" id="main-content">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-12">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              aria-label="Request to add a new event"
            >
              <Plus className="w-5 h-5 mr-2" />
              Request to Add Event
            </button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 gap-8">
              <EventSkeleton />
              <EventSkeleton />
              <EventSkeleton />
            </div>
          ) : (
            <div className="space-y-12">
              <Calendar events={events} onEventSelect={handleEventSelect} />
              
              {/* Upcoming Events List */}
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Upcoming Events</h2>
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-6">
                    {upcomingEvents.map(renderEventCard)}
                  </div>
                ) : (
                  <p className="text-gray-600 text-center py-8">
                    No upcoming events scheduled at this time.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <AddEventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEventAdded={fetchEvents}
      />

      {signup && (
        <SignupModal
          isOpen
          onClose={() => setSignup(null)}
          eventId={signup.event.id}
          eventTitle={signup.event.title}
          kind={signup.kind}
          onSignedUp={() => loadSignupCounts(events.map((e) => e.id))}
        />
      )}
    </>
  );
};

export default Events;